import React, { useState } from "react";
import { Alert, Badge, Button, Spinner, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { useAppApi, useAppContext } from "@/App";
import { useRequest } from "@components/AppHook";
import { DevForm } from "@components/Forms/Fields";
import { Awesome } from "@components/Helpers";
import { DefaultLayout } from "@components/Layouts";
import { ListingCard } from "@components/Pages";
import { url_reverse } from "@components/Utils";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const SortableRow = (props) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: props.id,
    });

    const style = {
        opacity: isDragging ? 0.4 : undefined,
        transform: CSS.Translate.toString(transform),
        transition,
    };

    return (
        <tr ref={setNodeRef} style={style} className={props.className}>
            <td {...listeners} {...attributes}>
                {props.id === props.sortingId ? (
                    <Spinner animation="border" variant="primary" size="sm" />
                ) : (
                    <Awesome type="reorder" />
                )}
            </td>
            {props.children}
        </tr>
    );
};

const TableField = ({ field, item }) => {
    const { apiData } = useAppContext();
    const { slug } = apiData.pocket;

    if (field.type === "link") {
        return <a href={url_reverse("admin:pockets:item", { id: item.id, slug })}>{field.val}</a>;
    }
    if (field.type === "check") {
        if (field.val) {
            return <Awesome type="check" color="green" />;
        }
    }
    if (field.type === "popover") {
        if (field.val) {
            return (
                <div>
                    <Awesome type="info-circle" tooltip={<span dangerouslySetInnerHTML={{ __html: field.val }} />} />
                </div>
            );
        }
    }

    return field.val;
};

const TableFieldPublished = ({ field }) => {
    return (
        <>
            {field.rules?.is_published_ww_gc && <Badge variant={"danger"}>GC</Badge>}
            {field.rules?.is_published_mobile && <Badge variant={"warning"}>MOB</Badge>}
            {field.rules?.is_published_vkplay && <Badge variant={"secondary"}>VKPLAY</Badge>}
            {field.rules?.is_published_catalog_vkplay && <Badge variant={"success"}>K. Web</Badge>}
            {field.rules?.is_published_catalog_ww_gc && <Badge variant={"info"}>K. GC</Badge>}
            {field.rules?.is_published_mini_vkplay && <Badge variant={"primary"}>MINI</Badge>}
        </>
    );
};

const TableActionButtons = ({ item, handleClone }) => {
    const { setDelData } = useAppContext();
    const { apiData } = useAppContext();
    const { slug } = apiData.pocket;

    return (
        <div className="d-flex justify-content-end">
            <DevForm.Button variant="outline-secondary" size="sm" name="clone" value={item.id} onClick={handleClone}>
                <Awesome type="clone" />
            </DevForm.Button>
            <Link
                to={url_reverse("admin:pockets:item", {
                    id: item.id,
                    slug,
                })}
                className="btn btn-sm btn-outline-primary ml-1"
            >
                <Awesome type="pencil" />
            </Link>
            <Button
                variant="outline-danger"
                size="sm"
                className="ml-1"
                onClick={() =>
                    setDelData({
                        url: url_reverse("api:pockets:delete_item", {
                            id: item.id,
                            slug,
                        }),
                    })
                }
            >
                <Awesome type="trash-o" />
            </Button>
        </div>
    );
};

export default function PocketsListItems() {
    const { slug } = useParams();
    const apiUrl = url_reverse("api:pockets:list_items", { slug });
    const { apiData, setApiData } = useAppApi(apiUrl);
    const { t } = useTranslation();
    const [sortingId, setSortingId] = useState(null);
    const sensors = useSensors(useSensor(PointerSensor));
    const { api_post } = useRequest();

    if (!apiData?.pocket) return;

    function handleDragEnd(event) {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = apiData.object_list.findIndex((item) => item.id === active.id);
            const newIndex = apiData.object_list.findIndex((item) => item.id === over.id);
            const new_list = arrayMove(apiData.object_list, oldIndex, newIndex);
            const ids = new_list.map((o) => o.id);

            const newApiData = { ...apiData };
            newApiData.object_list = new_list;
            setApiData(newApiData);

            setSortingId(active.id);

            api_post(null, url_reverse("api:pockets:list_items", { slug }), {
                params: { action: "rearrange", ids },
                then: (resp) => {
                    if (resp.status === "ok") {
                        const sorted = { ...apiData };
                        sorted.object_list = resp.object_list;
                        setApiData(sorted);
                    }
                },
                finally: () => setSortingId(null),
            });
        }
    }

    function handleClone(ev, id) {
        api_post(ev, url_reverse("api:pockets:list_items", { slug }), {
            params: { action: "clone", id },
            then: (resp) => {
                if (resp.status === "ok") {
                    setApiData(resp);
                }
            },
        });
    }

    const header = (
        <>
            <Link to={`${url_reverse("admin:pockets:list")}#group_${apiData?.pocket.pocket_group_id}`}>
                {t("Карман")}
            </Link>{" "}
            – {apiData?.pocket.name || ""}
        </>
    );

    const headerTools = (
        <>
            {apiData?.edit_info && (
                <Link to={url_reverse("admin:pockets:edit_info", { slug })} className="btn btn-sm btn-dark mr-2">
                    <Awesome type="cog" /> {t("Настройки")}
                </Link>
            )}
            <Link to={url_reverse("admin:pockets:item", { id: "add", slug })} className="btn btn-sm btn-dark">
                <Awesome type="plus-circle" /> {t("Добавить")}
            </Link>
        </>
    );

    return (
        <DefaultLayout>
            {apiData && (
                <ListingCard header={header} headerTools={headerTools}>
                    {/* Костыльное сообщение для кармана Акций */}
                    {apiData?.pocket.slug === "play2-promo" && (
                        <Alert variant={"warning"}>Для данного кармана первые 6 игр должны быть уникальными</Alert>
                    )}

                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <Table responsive hover className="dmg-table">
                            <thead>
                                <tr>
                                    <th style={{ width: "2%" }}></th>
                                    {apiData?.titles.map((title, index) => (
                                        <th key={`title-${index}`}>{title}</th>
                                    ))}
                                    <th style={{ width: "18%" }}>{t("Опубликовано")}</th>
                                    <th style={{ width: "9%" }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                <SortableContext items={apiData?.object_list} strategy={verticalListSortingStrategy}>
                                    {apiData?.object_list.map((item) => {
                                        return (
                                            <SortableRow
                                                id={item.id}
                                                key={item.id}
                                                className={item.table_row_class}
                                                sortingId={sortingId}
                                            >
                                                <>
                                                    {item.table_fields?.map((field, index) => (
                                                        <td key={`field-${index}`}>
                                                            <TableField field={field} item={item} />
                                                        </td>
                                                    ))}
                                                    <td>
                                                        <TableFieldPublished field={item} />
                                                    </td>
                                                    <td>
                                                        <TableActionButtons
                                                            handleClone={(ev) => handleClone(ev, item.id)}
                                                            item={item}
                                                        />
                                                    </td>
                                                </>
                                            </SortableRow>
                                        );
                                    })}
                                </SortableContext>
                            </tbody>
                        </Table>
                    </DndContext>
                </ListingCard>
            )}
        </DefaultLayout>
    );
}
